import { isEqual } from 'lodash';
import { getDefaultLazyStateFilters } from '../../../utils/getDefaultLazyStateFilters';
import DetailedFilter from './components/DetailedFilter/DetailedFilter';
import { ReferenceControl } from '../../../types/types';
import { useDetailFilterContext } from '../../../hooks/context/useDetailFilterContext';
import { useSelectedFiltersContext } from '../../../hooks/context/useSelectedFiltersContext';
import { useSelectedRowsContext } from '../../../hooks/context/useSelectedRowsContext';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { getCorrectSubmitFilters } from './utils/getCorrectSubmitFilters';
import { getDatasetDataApiRequestData } from '../../../api/utils/requestData/getDatasetDataApiRequestData';
import RequestDataProvider from '../../../context/RequestDataContext';
import { useMemo } from 'react';
import { useUserState } from '../../../hooks/context/useUserState';
import { useInterDatasetGuard } from '../../../pages/DatasetList/hooks/useInterDatasetGuard';
import { useDatasetSessionStorageContext } from '../../../hooks/context/useDatasetSessionStorageContext';

interface MasterFilterProps {
  closeFilter: (options?: { resetToDatasetList?: boolean }) => void;
  openFullscreenReference: (
    name: string,
    datasetId: string,
    control: ReferenceControl,
    value: string,
    label: string,
    resolvedFilter?: Record<string, any> | null
  ) => void;
}

const MasterFilter = ({
  closeFilter,
  openFullscreenReference,
}: MasterFilterProps) => {
  const { setDefaultMasterPopoverFilters, submitMasterPopoverFilters } =
    useDetailFilterContext();
  const {
    selectedFilters,
    setSelectedFilters,
    popoverFilters,
    setPopoverFilters,
  } = useSelectedFiltersContext();

  const {
    state: { token: sessionToken },
  } = useUserState();

  const {
    activeFilters,
    activeSorting,
    setActiveFilters,
    setLazyState,
    dataset,
    data,
    datasetControl,
    filter,
    referenceDatasets,
    setReferenceDatasets,
  } = useDatasetContext();

  const { resetSelectedRows } = useSelectedRowsContext();

  const { getDatasetSessionStorageState, setDatasetSessionStorageState } =
    useDatasetSessionStorageContext();

  // Handle filter first redirect on filter
  useInterDatasetGuard();

  // Persist submitted filterFirst filters keyed by datasetId so they can be
  // restored after a full reload (URL-bar edits within the same dataset) and
  // the user does not have to re-confirm the filter UI.
  const persistFilterFirstFilters = (apiObject: Record<string, unknown>) => {
    if (!dataset?.filterFirst || !dataset?.datasetId) return;
    const existing =
      getDatasetSessionStorageState().persistedFilterFirstFilters ?? {};
    setDatasetSessionStorageState({
      persistedFilterFirstFilters: {
        ...existing,
        [dataset.datasetId]: apiObject,
      },
    });
  };

  const requestData = useMemo(() => {
    if (!dataset) {
      return undefined;
    }
    return getDatasetDataApiRequestData(
      dataset,
      activeSorting,
      sessionToken,
      0,
      activeFilters as Record<string, any> | undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      datasetControl ?? undefined
    );
  }, [dataset, activeSorting, activeFilters, datasetControl, sessionToken]);

  const submitFilters = (filters: any = undefined) => {
    if (!dataset) return;
    const newFilters = getDefaultLazyStateFilters(dataset, filter);
    const correctFilters = getCorrectSubmitFilters(
      dataset,
      filters,
      selectedFilters,
      newFilters
    );

    setLazyState((oldLazyState) => {
      return isEqual(oldLazyState.filters, newFilters)
        ? oldLazyState
        : { ...oldLazyState, filters: newFilters };
    });

    const apiObject = {};
    correctFilters.forEach((item) => {
      if (item.datasetId === dataset?.datasetId) {
        apiObject[item.name] = { [item.matchMode]: item.value };
      } else {
        const subsetKey = `${item.datasetId}>`;
        if (apiObject[subsetKey]) {
          apiObject[subsetKey][item.name] = { [item.matchMode]: item.value };
        } else {
          apiObject[subsetKey] = {
            [item.name]: { [item.matchMode]: item.value },
          };
        }
      }
    });
    // Normalize null ≡ {} so "no filters → clear+close" doesn't flag changed.
    const hasChanged = !isEqual(activeFilters ?? {}, apiObject);

    // filterFirst init: trigger initial data load; closeFilter overrides via fromFilterFirstInit.
    if (data === undefined) {
      setActiveFilters(apiObject);
      persistFilterFirstFilters(apiObject);
      closeFilter({ resetToDatasetList: hasChanged });
      return;
    }

    if (!hasChanged) {
      persistFilterFirstFilters(apiObject);
      closeFilter({ resetToDatasetList: false });
      return;
    }

    resetSelectedRows();
    setActiveFilters(apiObject);
    persistFilterFirstFilters(apiObject);
    closeFilter({ resetToDatasetList: true });
  };

  return (
    <RequestDataProvider requestData={requestData}>
      <DetailedFilter
        submitFilters={submitFilters}
        filter={filter}
        popoverFilters={popoverFilters}
        setPopoverFilters={setPopoverFilters}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        setDefaultPopoverFilters={setDefaultMasterPopoverFilters}
        submitPopoverFilters={submitMasterPopoverFilters}
        dataset={dataset}
        referenceDatasets={referenceDatasets}
        setReferenceDatasets={setReferenceDatasets}
        openFullscreenReference={openFullscreenReference}
      />
    </RequestDataProvider>
  );
};

export default MasterFilter;
